/**
 * Dropdowns and date fields that look like the platform, not the operating system (Coleman, 2026-10-23:
 * "we need to make sure that all selectors like this, dates, etc., are native and look good and aligned
 * with the UI"). The browser's own <select> list and date calendar are system chrome: a white macOS menu,
 * a blue highlight, a calendar in someone else's font. These draw their own.
 *
 * Drop-in for the elements they replace, so a swap is one word:
 *   <select value onChange={e => ...} aria-label style>{<option value>label</option>...}</select>
 *   becomes <Select ...the same props...>{the same options}</Select>
 *   <input type="date" value onChange={e => ...} />  becomes  <DateInput ...the same props... />
 * onChange still gets an event with e.target.value. The trigger keeps the style it's given, so each
 * screen's field looks as it did, plus the platform's list or calendar when it opens.
 *
 * The real element stays in the page, visually hidden: screen readers, the keyboard (arrows still change
 * a select; Space, Enter or Alt+Down open ours instead of the system's) and tests use it as before.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const GOLD = "#E2DD9F";
// On a phone or tablet the hidden element must never take focus: iOS opens its own picker for a focused
// select or date input, even an invisible one (a tap on a wrapping <label> would do it). There it's left
// out of focus entirely; with a mouse and keyboard it stays, so Tab and the arrows work.
const coarse = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
const HIDDEN = { position: "absolute", opacity: 0, width: 1, height: 1, margin: 0, padding: 0, border: 0, overflow: "hidden", pointerEvents: "none", left: 0, top: 0 };

// A light field (dark text) gets a light list; everything else the dark one.
function isLightField(style = {}) {
  const c = String(style.color || "");
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c.trim());
  if (m) {
    const h = m[1].length === 3 ? m[1].split("").map(x => x + x).join("") : m[1];
    const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
    return (r * 299 + g * 587 + b * 114) / 1000 < 110;
  }
  const rgb = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(c);
  if (rgb) return (+rgb[1] * 299 + +rgb[2] * 587 + +rgb[3] * 114) / 1000 < 110;
  return false;
}
const PALETTE = {
  dark: { bg: "linear-gradient(180deg,#1A1A1A,#0E0E0E)", border: "rgba(255,255,255,0.1)", text: "#F5F5F5", dim: "#9CA3AF", hover: "rgba(255,255,255,0.06)", shadow: "0 16px 40px rgba(0,0,0,0.55)" },
  light: { bg: "#FFFFFF", border: "#E3E3E3", text: "#111111", dim: "#666666", hover: "rgba(0,0,0,0.05)", shadow: "0 12px 32px rgba(0,0,0,0.14)" },
};

/** Where the popover goes: under the field, or above it when there's no room below. Fixed, so a card
 *  with overflow hidden or a modal can't clip it. */
function usePlace(anchor, open, height) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current) return undefined;
    const place = () => {
      const r = anchor.current.getBoundingClientRect();
      const vh = window.innerHeight || 800, vw = window.innerWidth || 1200;
      const below = vh - r.bottom;
      const up = below < Math.min(height, 320) + 12 && r.top > below;
      const left = Math.max(8, Math.min(r.left, vw - Math.max(r.width, 180) - 8));
      setPos({ left, minWidth: r.width, ...(up ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 }), maxHeight: Math.max(160, (up ? r.top : below) - 16) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, anchor, height]);
  return pos;
}

function useDismiss(open, setOpen, refs) {
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (!refs.some(r => r.current && r.current.contains(e.target))) setOpen(false); };
    document.addEventListener("mousedown", down);
    document.addEventListener("touchstart", down);
    return () => { document.removeEventListener("mousedown", down); document.removeEventListener("touchstart", down); };
  }, [open, setOpen, refs]);
}

// Options from <option> / <optgroup> children, flattened.
function readOptions(children) {
  const out = [];
  const walk = (kids, group) => React.Children.forEach(kids, (c) => {
    if (!c || typeof c !== "object") return;
    if (c.type === React.Fragment) { walk(c.props.children, group); return; }
    if (c.type === "optgroup") { out.push({ header: c.props.label }); walk(c.props.children, c.props.label); return; }
    if (c.type === "option") {
      const label = React.Children.toArray(c.props.children).join("");
      out.push({ value: c.props.value !== undefined ? String(c.props.value) : label, label, disabled: !!c.props.disabled, group });
    }
  });
  walk(children);
  return out;
}

// The trigger keeps the field's look; these never apply to it.
const stripNative = ({ appearance, WebkitAppearance, MozAppearance, ...rest } = {}) => rest;

/** A dropdown. Same props as <select>; `options` may be given instead of <option> children. */
export function Select({ value, defaultValue, onChange, children, options: given, style, className, disabled, placeholder = "Select", id, name, title, ...aria }) {
  const options = useMemo(() => given ? given.map(o => (typeof o === "object" ? { ...o, value: String(o.value) } : { value: String(o), label: String(o) })) : readOptions(children), [given, children]);
  const [inner, setInner] = useState(defaultValue != null ? String(defaultValue) : (options.find(o => !o.header)?.value ?? ""));
  const current = value !== undefined && value !== null ? String(value) : inner;
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const touch = useMemo(coarse, []);
  const box = useRef(null), pop = useRef(null), native = useRef(null);
  const pal = PALETTE[isLightField(style) ? "light" : "dark"];
  const pos = usePlace(box, open, options.length * 34);
  useDismiss(open, setOpen, [box, pop]);
  const chosen = options.find(o => !o.header && o.value === current);
  const choose = (v) => {
    setOpen(false);
    if (v === current) return;
    if (value === undefined) setInner(v);
    // The native element moves too, so its change event (and anything listening for it) is the real one.
    onChange?.({ target: { value: v, name }, currentTarget: { value: v, name }, preventDefault() {}, stopPropagation() {} });
    if (!touch) native.current?.focus?.();
  };
  const selectable = options.map((o, i) => (o.header || o.disabled ? -1 : i)).filter(i => i >= 0);
  useEffect(() => { if (open) setHi(options.findIndex(o => !o.header && o.value === current)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || hi < 0 || !pop.current) return;
    pop.current.querySelector(`[data-i="${hi}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, hi]);
  const step = (d) => { const at = selectable.indexOf(hi); setHi(selectable[Math.max(0, Math.min(selectable.length - 1, (at < 0 ? -1 : at) + d))] ?? -1); };
  const onKey = (e) => {
    if (disabled) return;
    if (!open && (e.key === " " || e.key === "Enter" || (e.altKey && e.key === "ArrowDown"))) { e.preventDefault(); setOpen(true); return; }
    if (!open) return;      // arrows change the native select, as they always did
    if (e.key === "Escape" || e.key === "Tab") { if (e.key === "Escape") e.preventDefault(); setOpen(false); }
    else if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (options[hi] && !options[hi].header && !options[hi].disabled) choose(options[hi].value); }
  };
  const trigger = stripNative(style);
  // The list is portalled, but React still bubbles its mousedown to the trigger, which would toggle it shut before
  // the click lands (2026-10-24: "this picker doesnt seem to be working"). The list stops it.
  return (
    <span ref={box} className={className} title={title}
      style={{ position: "relative", display: trigger.display === "block" || trigger.width === "100%" ? "flex" : "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1, boxSizing: "border-box", minWidth: 0, fontFamily: "inherit", ...trigger, ...(open ? { borderColor: `${GOLD}88` } : {}) }}
      onMouseDown={(e) => { if (disabled) return; e.preventDefault(); if (!touch) native.current?.focus?.(); setOpen(o => !o); }}>
      <span aria-hidden="true" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", color: chosen ? "inherit" : pal.dim }}>{chosen ? chosen.label : placeholder}</span>
      <ChevronDown aria-hidden="true" size={13} style={{ flexShrink: 0, opacity: 0.6, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}/>
      <select ref={native} id={id} name={name} value={current} disabled={disabled || touch} tabIndex={touch ? -1 : 0} {...aria}
        onChange={(e) => { if (value === undefined) setInner(e.target.value); onChange?.(e); }} onKeyDown={onKey} style={HIDDEN}>
        {options.filter(o => !o.header).map(o => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
      {open && pos && createPortal(
        <div ref={pop} role="presentation" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{ position: "fixed", zIndex: 10050, left: pos.left, top: pos.top, bottom: pos.bottom, minWidth: Math.max(pos.minWidth, 140), maxWidth: 360, maxHeight: Math.min(320, pos.maxHeight),
            overflowY: "auto", background: pal.bg, border: `1px solid ${pal.border}`, borderRadius: 12, boxShadow: pal.shadow, padding: 4, fontFamily: "inherit" }}>
          {options.map((o, i) => o.header ? (
            <div key={`h${i}`} style={{ padding: "8px 10px 4px", fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: pal.dim }}>{o.header}</div>
          ) : (
            <div key={o.value + i} data-i={i} onMouseEnter={() => setHi(i)} onClick={() => !o.disabled && choose(o.value)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, fontSize: 13, cursor: o.disabled ? "default" : "pointer",
                color: o.value === current ? GOLD : pal.text, fontWeight: o.value === current ? 700 : 500, opacity: o.disabled ? 0.4 : 1,
                background: i === hi ? pal.hover : "transparent", whiteSpace: "nowrap" }}>
              <span style={{ width: 14, display: "inline-flex", flexShrink: 0 }}>{o.value === current && <Check size={12} color={GOLD}/>}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
            </div>
          ))}
        </div>, document.body)}
    </span>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || "")); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

/** A date field. Same props as <input type="date"> (value "YYYY-MM-DD", min, max, onChange(e)). */
export function DateInput({ value, onChange, style, className, disabled, placeholder = "Pick a date", min, max, id, name, title, type, ...aria }) {
  const [open, setOpen] = useState(false);
  const picked = fromIso(value);
  const [view, setView] = useState(() => picked || new Date());
  const touch = useMemo(coarse, []);
  const [hi, setHi] = useState(null);
  const box = useRef(null), pop = useRef(null), native = useRef(null);
  const pal = PALETTE[isLightField(style) ? "light" : "dark"];
  const pos = usePlace(box, open, 330);
  useDismiss(open, setOpen, [box, pop]);
  useEffect(() => { if (open) { const d = fromIso(value) || new Date(); setView(d); setHi(d); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const lo = fromIso(min), up = fromIso(max);
  const allowed = (d) => (!lo || d >= lo) && (!up || d <= up);
  const emit = (v) => onChange?.({ target: { value: v, name }, currentTarget: { value: v, name }, preventDefault() {}, stopPropagation() {} });
  const choose = (d) => { if (d && !allowed(d)) return; setOpen(false); emit(d ? toIso(d) : ""); if (!touch) native.current?.focus?.(); };
  const y = view.getFullYear(), m = view.getMonth();
  const first = new Date(y, m, 1);
  const cells = Array.from({ length: 42 }, (_, i) => new Date(y, m, 1 - first.getDay() + i));
  const today = new Date(); const todayIso = toIso(today);
  const onKey = (e) => {
    if (disabled) return;
    if (!open && (e.key === " " || e.key === "Enter" || (e.altKey && e.key === "ArrowDown"))) { e.preventDefault(); setOpen(true); return; }
    if (!open) return;
    const move = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (move) { e.preventDefault(); const d = new Date(hi || view); d.setDate(d.getDate() + move); setHi(d); setView(d); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (hi) choose(hi); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Tab") setOpen(false);
  };
  const label = picked ? picked.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : placeholder;
  const navBtn = { background: "none", border: "none", color: pal.text, cursor: "pointer", padding: 4, borderRadius: 6, display: "inline-flex" };
  const trigger = stripNative(style);
  return (
    <span ref={box} className={className} title={title}
      style={{ position: "relative", display: trigger.width === "100%" ? "flex" : "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
        boxSizing: "border-box", minWidth: 0, fontFamily: "inherit", ...trigger, ...(open ? { borderColor: `${GOLD}88` } : {}) }}
      onMouseDown={(e) => { if (disabled) return; e.preventDefault(); if (!touch) native.current?.focus?.(); setOpen(o => !o); }}>
      <span aria-hidden="true" style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: picked ? "inherit" : pal.dim }}>{label}</span>
      <Calendar aria-hidden="true" size={14} style={{ flexShrink: 0, opacity: 0.6 }}/>
      <input ref={native} type="date" id={id} name={name} value={value || ""} min={min} max={max} disabled={disabled || touch} tabIndex={touch ? -1 : 0} {...aria}
        onChange={(e) => onChange?.(e)} onKeyDown={onKey} style={HIDDEN}/>
      {open && pos && createPortal(
        <div ref={pop} role="presentation" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{ position: "fixed", zIndex: 10050, left: pos.left, top: pos.top, bottom: pos.bottom, width: 264, background: pal.bg, border: `1px solid ${pal.border}`, borderRadius: 14,
            boxShadow: pal.shadow, padding: 12, color: pal.text, fontFamily: "inherit" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <button type="button" aria-label="Previous month" style={navBtn} onClick={() => setView(new Date(y, m - 1, 1))}><ChevronLeft size={16}/></button>
            <span style={{ flex: 1, textAlign: "center", fontSize: 13.5, fontWeight: 700 }}>{first.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
            <button type="button" aria-label="Next month" style={navBtn} onClick={() => setView(new Date(y, m + 1, 1))}><ChevronRight size={16}/></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, textAlign: "center" }}>
            {DAYS.map((d, i) => <span key={i} style={{ fontSize: 10.5, fontWeight: 700, color: pal.dim, padding: "4px 0" }}>{d}</span>)}
            {cells.map((d) => {
              const iso = toIso(d), inMonth = d.getMonth() === m, sel = value === iso, ok = allowed(d), focus = hi && toIso(hi) === iso;
              return (
                <button type="button" key={iso} disabled={!ok} onClick={() => choose(d)} aria-label={d.toDateString()}
                  style={{ height: 32, borderRadius: 8, border: iso === todayIso && !sel ? `1px solid ${GOLD}66` : "1px solid transparent", fontFamily: "inherit", fontSize: 12.5,
                    fontWeight: sel ? 800 : 500, cursor: ok ? "pointer" : "default", background: sel ? GOLD : focus ? pal.hover : "transparent",
                    color: sel ? "#111" : !ok ? `${pal.dim}66` : inMonth ? pal.text : pal.dim }}>{d.getDate()}</button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <button type="button" style={{ ...navBtn, fontSize: 12, fontWeight: 700, color: pal.dim }} onClick={() => choose(null)}>Clear</button>
            <button type="button" style={{ ...navBtn, fontSize: 12, fontWeight: 700, color: GOLD }} onClick={() => choose(today)}>Today</button>
          </div>
        </div>, document.body)}
    </span>
  );
}
